/**
 * FreshCards Theme Configuration
 * Centralized theme definitions and management
 */

export interface ThemeColors {
  // Background colors
  bg: {
    primary: string;
    secondary: string;
    tertiary: string;
    hover: string;
    sidebar: string;
    surface: string;
  };
  
  // Text colors
  text: {
    primary: string;
    secondary: string;
    tertiary: string;
    muted: string;
    disabled: string;
    inverse: string;
  };
  
  // Border colors
  border: {
    primary: string;
    secondary: string;
    tertiary: string;
    focused: string;
    error: string;
  };
  
  // Accent colors
  accent: {
    primary: string;
    secondary: string;
    hover: string;
    muted: string;
    strong: string;
  };
  
  // Status colors
  status: {
    success: string;
    warning: string;
    error: string;
    info: string;
    pending: string;
  };
}

export interface ThemeTypography {
  fontFamily: {
    base: string;
    mono: string;
    heading: string;
  };
  fontSize: {
    xs: string;
    sm: string;
    base: string;
    lg: string;
    xl: string;
    '2xl': string;
    '3xl': string;
    '4xl': string;
  };
  fontWeight: {
    light: number;
    normal: number;
    medium: number;
    semibold: number;
    bold: number;
  };
  lineHeight: {
    tight: number;
    normal: number;
    relaxed: number;
  };
}

export interface ThemeSpacing {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  '2xl': string;
  '3xl': string;
  '4xl': string;
}

export interface ThemeBorderRadius {
  none: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  full: string;
}

export interface ThemeShadows {
  sm: string;
  md: string;
  lg: string;
  xl: string;
  none: string;
}

export interface ThemeZIndex {
  dropdown: number;
  tooltip: number;
  modal: number;
  popover: number;
  toast: number;
  notification: number;
  surface: number;
}

export interface ThemeAnimations {
  duration: {
    fast: string;
    normal: string;
    slow: string;
  };
  easing: {
    linear: string;
    ease: string;
    easeIn: string;
    easeOut: string;
    easeInOut: string;
  };
}

export interface ThemeBreakpoints {
  sm: string;
  md: string;
  lg: string;
  xl: string;
  '2xl': string;
}

export interface Theme {
  name: string;
  colors: ThemeColors;
  typography: ThemeTypography;
  spacing: ThemeSpacing;
  borderRadius: ThemeBorderRadius;
  shadows: ThemeShadows;
  zIndex: ThemeZIndex;
  animations: ThemeAnimations;
  breakpoints: ThemeBreakpoints;
}

export const lightTheme: Theme = {
  name: 'light',
  colors: {
    bg: {
      primary: '#ffffff',
      secondary: '#fafafa',
      tertiary: '#f7f7f7',
      hover: '#f0f0f0',
      sidebar: '#ffffff',
      surface: '#ffffff',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#525252',
      tertiary: '#8e8e8e',
      muted: '#9ca3af',
      disabled: '#d1d5db',
      inverse: '#ffffff',
    },
    border: {
      primary: '#e5e5e5',
      secondary: '#e5e5e5',
      tertiary: '#f0f0f0',
      focused: '#3b82f6',
      error: '#ef4444',
    },
    accent: {
      primary: '#3b82f6',
      secondary: '#60a5fa',
      hover: '#2563eb',
      muted: '#dbeafe',
      strong: '#1d4ed8',
    },
    status: {
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6',
      pending: '#f59e0b',
    },
  },
  typography: {
    fontFamily: {
      base: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      mono: '"SF Mono", "Monaco", "Inconsolata", "Roboto Mono", "Source Code Pro", Menlo, Consolas, "Courier New", monospace',
      heading: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    },
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },
  spacing: {
    xs: '2px',
    sm: '4px',
    md: '8px',
    lg: '16px',
    xl: '24px',
    '2xl': '32px',
    '3xl': '48px',
    '4xl': '64px',
  },
  borderRadius: {
    none: '0px',
    sm: '3px',
    md: '6px',
    lg: '8px',
    xl: '12px',
    full: '9999px',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    lg: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    xl: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    none: 'none',
  },
  zIndex: {
    dropdown: 1000,
    tooltip: 1070,
    modal: 1100,
    popover: 1200,
    toast: 1210,
    notification: 1220,
    surface: 100,
  },
  animations: {
    duration: {
      fast: '150ms',
      normal: '300ms',
      slow: '500ms',
    },
    easing: {
      linear: 'linear',
      ease: 'ease',
      easeIn: 'ease-in',
      easeOut: 'ease-out',
      easeInOut: 'ease-in-out',
    },
  },
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
};

export const darkTheme: Theme = {
  name: 'dark',
  colors: {
    bg: {
      primary: '#2d2d2d',
      secondary: '#262626',
      tertiary: '#1f1f1f',
      hover: '#404040',
      sidebar: '#2d2d2d',
      surface: '#2d2d2d',
    },
    text: {
      primary: '#ffffff',
      secondary: '#a3a3a3',
      tertiary: '#737373',
      muted: '#525252',
      disabled: '#404040',
      inverse: '#2d2d2d',
    },
    border: {
      primary: '#404040',
      secondary: '#404040',
      tertiary: '#333333',
      focused: '#60a5fa',
      error: '#f87171',
    },
    accent: {
      primary: '#60a5fa',
      secondary: '#93bbfc',
      hover: '#3b82f6',
      muted: '#1e3a8a',
      strong: '#3b82f6',
    },
    status: {
      success: '#34d399',
      warning: '#fbbf24',
      error: '#f87171',
      info: '#60a5fa',
      pending: '#fbbf24',
    },
  },
  typography: {
    fontFamily: {
      base: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      mono: '"SF Mono", "Monaco", "Inconsolata", "Roboto Mono", "Source Code Pro", Menlo, Consolas, "Courier New", monospace',
      heading: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    },
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },
  spacing: {
    xs: '2px',
    sm: '4px',
    md: '8px',
    lg: '16px',
    xl: '24px',
    '2xl': '32px',
    '3xl': '48px',
    '4xl': '64px',
  },
  borderRadius: {
    none: '0px',
    sm: '3px',
    md: '6px',
    lg: '8px',
    xl: '12px',
    full: '9999px',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    lg: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    xl: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    none: 'none',
  },
  zIndex: {
    dropdown: 1000,
    tooltip: 1070,
    modal: 1100,
    popover: 1200,
    toast: 1210,
    notification: 1220,
    surface: 100,
  },
  animations: {
    duration: {
      fast: '150ms',
      normal: '300ms',
      slow: '500ms',
    },
    easing: {
      linear: 'linear',
      ease: 'ease',
      easeIn: 'ease-in',
      easeOut: 'ease-out',
      easeInOut: 'ease-in-out',
    },
  },
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
};

export const themes = {
  light: lightTheme,
  dark: darkTheme,
} as const;

export type ThemeName = keyof typeof themes;

// ─────────────────────────────────────────────────────────────────────────────
// SKINS
//
// A *skin* is a product-level palette overlay that sits on top of the base
// light/dark *modes*. FreshCards and FreshVibe Studio each ship with their
// own skin; the same mode (light/dark) renders inside either skin.
//
// Each skin overrides only the brand-aware tokens — the structural tokens
// (spacing, radii, shadows, typography) are shared. The light/dark mode
// `themes` above are the canonical structural templates; skins are merged
// on top of them at runtime via `applySkinToTheme` in theme-context.tsx.
// ─────────────────────────────────────────────────────────────────────────────

/** A skin overrides brand-aware tokens of a base mode theme. */
export interface Skin {
  /** Stable identifier — used for persistence and URLs. */
  id: string;
  /** Human-readable name shown in the skin picker. */
  name: string;
  /** Which product this skin belongs to. */
  product: 'freshcards' | 'freshvibe-studio' | 'shared';
  /** Short description for the picker UI. */
  description?: string;
  /** Accent palette — replaces the `accent.*` keys of the base theme. */
  accent: ThemeColors['accent'];
  /** Border-focused color — replaces `border.focused` of the base theme. */
  borderFocused: string;
  /** Primary surface gradient (used for hero CTAs). Two stops. */
  primaryGradient: [string, string];
}

/**
 * Skins are defined declaratively in `app-overlays/<overlay-id>/accent.json`
 * (V4 § 2.1.6 — App-Overlays). This file loads them at build time and
 * re-exports them as the `Skin` TypeScript type.
 *
 * The JSON in `app-overlays/` is the source of truth. If you edit the JSON,
 * the runtime picks it up automatically on next dev-server start.
 *
 * Why this path: V4 mandates that overlays live outside the deployed app
 * (`app/`) so that swapping the overlay does not require re-bundling.
 */

// @ts-ignore — Vite handles JSON imports
import slateAccent from '../../../app-overlays/freshcards-slate/accent.json';
// @ts-ignore
import studioAccent from '../../../app-overlays/freshvibe-studio/accent.json';

/**
 * Default skin for FreshCards. Loaded from
 * `app-overlays/freshcards-slate/accent.json` (V4 source of truth).
 */
export const freshcardsSlateSkin: Skin = {
  id: slateAccent.id,
  name: slateAccent.name,
  product: slateAccent.product,
  description: slateAccent.description,
  accent: slateAccent.accent,
  borderFocused: slateAccent.borderFocused,
  primaryGradient: slateAccent.primaryGradient,
};

/**
 * Placeholder skin for FreshVibe Studio. Loaded from
 * `app-overlays/freshvibe-studio/accent.json`.
 */
export const freshvibeStudioSkin: Skin = {
  id: studioAccent.id,
  name: studioAccent.name,
  product: studioAccent.product,
  description: studioAccent.description,
  accent: studioAccent.accent,
  borderFocused: studioAccent.borderFocused,
  primaryGradient: studioAccent.primaryGradient,
};

export const skins = {
  'freshcards-slate': freshcardsSlateSkin,
  'freshvibe-studio': freshvibeStudioSkin,
} as const;

export type SkinId = keyof typeof skins;
export const DEFAULT_SKIN: SkinId = 'freshcards-slate';